import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import CheckList from "../CheckList";
import {
  RecorderTypePermissionWithoutRole,
  UserGroupRecorderPermission,
} from "api/interfaces/userInterface.interface";
import SelectComponent from "components/atoms/select/SelectComponent";
import {
  recorderLevelOptions,
  spectrumRoleLevelOptions,
  type Option,
} from "utils/options";
import ShowToggle from "components/blocks/modal/toggle/show/ShowToggle";
import { ValidateWord } from "components/atoms/text/Text";
import { RecorderType } from "../PermissionPolicy";

interface Props {
  recorderPermission: UserGroupRecorderPermission;
  permissionDisabled: boolean;
  onChangeRecorderLevel: (value: number, type: RecorderType) => void;
  disableList: RecorderTypePermissionWithoutRole;
  onChangeRecorderPermission: (
    type: "spectrum" | "vmax" | "cova",
    e: any
  ) => void;
  selectedRecorderTypes: string[];
  isNoRecorder: boolean;
}
const recorderPermissionCheckList = {
  spectrum: [
    {
      title: "spectrum",
      check: [
        {
          name: "viewLive",
          label: "View Live",
        },
        {
          name: "viewArchive",
          label: "View Archive",
        },
        {
          name: "exportArchive",
          label: "Export Archive",
        },
        {
          name: "viewBookmark",
          label: "View Bookmarks",
        },
        {
          name: "modifyBookmark",
          label: "Manage Bookmarks",
        },
        {
          name: "cameraUserInput",
          label: "User Input",
          extra: "(PTZ, Output, 2-Way Audio)",
        },
        {
          name: "cameraSetting",
          label: "Edit Settings",
        },
      ],
    },
  ],

  vmax: [
    {
      title: "vmax",
      check: [
        {
          name: "system",
          label: "System",
        },
        {
          name: "device",
          label: "Device",
        },
        {
          name: "alarm",
          label: "Alarm",
        },
        {
          name: "record",
          label: "Record",
        },
        {
          name: "network",
          label: "Network",
        },
        {
          name: "export",
          label: "Export",
        },
        {
          name: "shutdown",
          label: "Shutdown",
        },
        {
          name: "ptz",
          label: "PTZ",
        },
        {
          name: "playback",
          label: "Playback",
        },
        {
          name: "backup",
          label: "Backup",
        },
        // {
        //   name: "search",
        //   label: "Log",
        // },
      ],
    },
  ],

  cova: [
    {
      title: "cova",
      check: [
        {
          name: "localCamera",
          label: "Local Camera",
        },
        {
          name: "remoteCamera",
          label: "Remote Camera",
        },
        {
          name: "disk",
          label: "Disk",
        },
        {
          name: "aiEvent",
          label: "AI / Event",
        },
        {
          name: "network",
          label: "Network",
        },
        {
          name: "schedule",
          label: "Schedule",
        },
        {
          name: "record",
          label: "Record",
        },
        {
          name: "localSystem",
          label: "Local System",
        },
        {
          name: "remoteSystem",
          label: "Remote System",
        },
        {
          name: "accountAuthority",
          label: "Account and Authority",
        },
        {
          name: "remoteLogin",
          label: "Remote Login",
        },
        {
          name: "talkback",
          label: "Two-way audio",
        },
      ],
    },
  ],
};
function RecorderPermissionStep(props: Props): JSX.Element {
  const [permissionToggle, setPermissionToggle] = useState<{
    [key: string]: boolean;
  }>({
    spectrum: true,
    cova: true,
    vmax: true,
  });

  const exceptRoleField = (permission: any): Record<string, boolean> => {
    return Object.entries(permission).reduce<Record<string, boolean>>(
      (acc, [key, value]) => {
        if (typeof value === "boolean") {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );
  };

  const onChangeCollapse = useCallback(
    (name: string) => {
      setPermissionToggle({
        ...permissionToggle,
        [name]: !permissionToggle[name],
      });
    },
    [permissionToggle]
  );

  const onChangeRecorderLevel = (value: string, type: RecorderType) => {
    props.onChangeRecorderLevel(Number(String(value)), type);
  };

  const isDisabled = props.permissionDisabled || props.isNoRecorder;
  const getRoleSelectValue = useCallback(
    (options: Option[], role: number, fallback: string) => {
      const wanted = String(role);
      const exists = options.some((opt) => opt.value === wanted);
      return exists ? wanted : fallback;
    },
    []
  );
  const recorderPermissionList = useMemo(() => {
    const lists = [
      {
        title: "Spectrum Permissions",
        value: true,
        isShow: permissionToggle.spectrum,
        name: "spectrum",
        children: (
          <>
            <ModalInput label="Role">
              <SelectComponent
                options={spectrumRoleLevelOptions}
                onChange={(value) => onChangeRecorderLevel(value, "spectrum")}
                value={getRoleSelectValue(
                  spectrumRoleLevelOptions,
                  props.recorderPermission.spectrum.role,
                  "0"
                )}
                disabled={isDisabled}
                data-testid="users-user-group-steps-selects-change-recorder-level"
              />
            </ModalInput>
            <CheckList
              isNoTitle
              list={recorderPermissionCheckList.spectrum}
              isCheck={exceptRoleField(props.recorderPermission.spectrum)}
              isDisabled={exceptRoleField(props.disableList.spectrum)}
              onCheckedElement={(e: ChangeEvent<HTMLInputElement>) =>
                props.onChangeRecorderPermission("spectrum", e)
              }
            />
          </>
        ),
      },
      {
        title: "COVA Permissions",
        value: true,
        isShow: permissionToggle.cova,
        name: "cova",
        children: (
          <>
            <ModalInput label="Role">
              <SelectComponent
                options={recorderLevelOptions}
                onChange={(value) => onChangeRecorderLevel(value, "cova")}
                value={getRoleSelectValue(
                  recorderLevelOptions,
                  props.recorderPermission.cova.role,
                  "99"
                )}
                disabled={isDisabled}
                data-testid="users-user-group-steps-selects-change-recorder-level-2"
              />
            </ModalInput>
            <CheckList
              isNoTitle
              list={recorderPermissionCheckList.cova}
              isCheck={exceptRoleField(props.recorderPermission.cova)}
              isDisabled={exceptRoleField(props.disableList.cova)}
              onCheckedElement={(e: ChangeEvent<HTMLInputElement>) =>
                props.onChangeRecorderPermission("cova", e)
              }
            />
          </>
        ),
      },
      {
        title: "VMAX Permissions",
        value: true,
        isShow: permissionToggle.vmax,
        name: "vmax",
        children: (
          <>
            <ModalInput label="Role">
              <SelectComponent
                options={recorderLevelOptions}
                onChange={(value) => onChangeRecorderLevel(value, "vmax")}
                value={getRoleSelectValue(
                  recorderLevelOptions,
                  props.recorderPermission.vmax.role,
                  "99"
                )}
                disabled={isDisabled}
                data-testid="users-user-group-steps-selects-change-recorder-level-3"
              />
            </ModalInput>
            <CheckList
              isNoTitle
              list={recorderPermissionCheckList.vmax}
              isCheck={exceptRoleField(props.recorderPermission.vmax)}
              isDisabled={exceptRoleField(props.disableList.vmax)}
              onCheckedElement={(e: ChangeEvent<HTMLInputElement>) =>
                props.onChangeRecorderPermission("vmax", e)
              }
            />
          </>
        ),
      },
    ];

    let returnList = lists.filter((list) =>
      props.selectedRecorderTypes.includes(list.name.toUpperCase())
    );

    return returnList;
  }, [
    permissionToggle.spectrum,
    permissionToggle.cova,
    permissionToggle.vmax,
    recorderPermissionCheckList,
    props.recorderPermission,
    props.disableList,
    props.selectedRecorderTypes,
    props.onChangeRecorderPermission,
    isDisabled,
    getRoleSelectValue,
  ]);

  return (
    <>
      {props.isNoRecorder && (
        <ValidateWord data-testid="users-user-group-steps-validate-no-recorder">
          This organization has no recorders, so you need to apply all.
        </ValidateWord>
      )}

      <ShowToggle
        isRecorderPermission
        items={recorderPermissionList}
        onToggle={onChangeCollapse}
      />
    </>
  );
}

export default RecorderPermissionStep;
